import {
  createRouter,
  createWebHashHistory,
  type RouteRecordRaw,
} from "vue-router";
import { ref } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useCurrentUser } from "@/composables/useCurrentUser";
import { checkModuleAccess } from "@/core/helpers/moduleAccess";
import { isRoleAllowed } from "./roleGuard";
import { readUserData } from "@/services/authStorage";

import {
  signaturePlatformPublicRoutes,
  signaturePlatformAuthRoutes,
} from "./routes/signaturePlatform";
import {
  businessAutomationPublicRoutes,
  businessAutomationAuthRoutes,
} from "./routes/businessAutomation";
import {
  publicCertificateRoutes,
  refusedOnVerifyPage,
} from "./routes/publicCertificate";
import { certificationRoutes } from "./routes/certification";
import { reportsPublicRoutes, reportsAuthRoutes } from "./routes/reports";
import { manageDataRoutes } from "./routes/manageData";
import { adminRoutes } from "./routes/admin";
import { expenseTrackerRoutes } from "./routes/expenseTracker";
import { scholarEngagementRoutes } from "./routes/scholarEngagement";
import { reviewRequestsRoutes } from "./routes/reviewRequests";
import { isPageChange } from "./pageTransition";

// Route meta contract. Declaring the known keys lets the guard read them
// type-safely (no casts) and stops a typo like `moduleKy` compiling silently.
declare module "vue-router" {
  interface RouteMeta {
    requiresAuth?: boolean;
    // Access-grant key from ModulesConfig; gates the route on that module.
    moduleKey?: string;
  }
}

// Reactive signal for RouteTransitionOverlay — replaces MutationObserver approach
export const isRouteTransitioning = ref(false);

const routes: Array<RouteRecordRaw> = [
  // Public routes — no auth required
  ...signaturePlatformPublicRoutes,
  ...businessAutomationPublicRoutes,
  ...reportsPublicRoutes,
  ...publicCertificateRoutes,

  // Auth shell — all children inherit requiresAuth: true
  {
    path: "/",
    redirect: "/dashboard",
    component: () => import("@/layout/Layout.vue"),
    meta: { requiresAuth: true },
    children: [
      // Exception Approval — internal-only; requires auth despite URL token
      {
        path: "/exception-approval/:token",
        name: "exception-approval",
        component: () =>
          import("@/views/ExceptionApproval/ExceptionApproval.vue"),
        props: true,
      },
      // Dashboard
      {
        path: "/dashboard",
        name: "dashboard",
        component: () => import("@/views/Dashboard/Dashboard.vue"),
      },
      // Downloads Page
      {
        path: "/downloads",
        name: "Downloads",
        component: () => import("@/views/Downloads/Downloads.vue"),
      },
      ...certificationRoutes,
      ...reportsAuthRoutes,
      ...manageDataRoutes,
      ...reviewRequestsRoutes,
      ...adminRoutes,
      ...signaturePlatformAuthRoutes,
      ...businessAutomationAuthRoutes,
      ...expenseTrackerRoutes,
      ...scholarEngagementRoutes,
    ],
  },
  {
    path: "/sign-in",
    name: "sign-in",
    component: () => import("@/views/Authentication/SignIn.vue"),
  },
  {
    path: "/404",
    name: "404",
    component: () => import("@/views/Authentication/Error404.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/404",
  },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

router.beforeEach((to, from, next) => {
  if (refusedOnVerifyPage(window.location.pathname, to.name)) {
    next(false);
    return;
  }
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    try {
      const authStore = useAuthStore();
      const isAuthenticated = authStore.checkAuth();
      if (isAuthenticated) {
        // Role gate first: it is a hard deny on identity, so it must not be
        // reachable past a module grant. `requiresRole` is inherited down the
        // matched chain by `crudRoutes`.
        const required = to.meta.requiresRole;
        const role = readUserData()?.role ?? "";
        if (!isRoleAllowed(required, role)) {
          // Authenticated but wrong role — bounce to dashboard, don't leak the screen.
          // `dashboard` carries no requiresRole, so this cannot loop.
          next({ name: "dashboard" });
          return;
        }
        // Module-access gate: a route may declare `meta.moduleKey`; the user must
        // hold that grant (super-admins bypass, mirroring the backend
        // `require_module`). Without this a deep link — e.g. the emailed Financial
        // Obligations reminder — would render the shell for a user whose grant was
        // revoked, only to 403 on every API call. Send them to the dashboard, the
        // same place the sidebar already routes an inaccessible module to.
        const moduleKey = to.matched.find((r) => r.meta.moduleKey)?.meta
          .moduleKey;
        if (moduleKey) {
          const { isSuperAdmin, userModules } = useCurrentUser();
          if (!isSuperAdmin && !checkModuleAccess(moduleKey, userModules)) {
            next({ name: "dashboard" });
            return;
          }
        }
        // Show route transition overlay -- not on the initial load, and not
        // for a query-only change on the same page (`pageTransition.ts`).
        if (isPageChange(to, from)) {
          isRouteTransitioning.value = true;
        }
        next();
      } else {
        authStore.logout();
        // Carry the intended destination through the login round-trip, so an
        // emailed deep link lands where it pointed instead of the dashboard.
        // SignIn.vue validates this before using it (see resolveRedirectTarget).
        next({ name: "sign-in", query: { redirect: to.fullPath } });
      }
    } catch {
      useAuthStore().logout();
      next({ name: "sign-in", query: { redirect: to.fullPath } });
    }
  } else {
    next(); // Allow navigation if not requiring authentication
  }
});

router.afterEach(() => {
  // Single rAF to ensure new page has painted before dismissing progress bar
  requestAnimationFrame(() => {
    isRouteTransitioning.value = false;
  });
});

export default router;
